/**
 * Blog data. Each post has structured content (no markdown parser dependency).
 * Add new posts here. SEO meta + Article JSON-LD generated automatically.
 *
 * Content blocks:
 *   { type: "p",  text }
 *   { type: "h2", text }
 *   { type: "h3", text }
 *   { type: "list", items: [string] }
 *   { type: "callout", text }
 *   { type: "image", src, alt, caption }
 */

const ASSET = "/img";

export const BLOG_POSTS = [
  {
    slug: "mississauga-office-wrap-2-days",
    title: "How We Wrapped a Mississauga Office in 2 Days — Before & After",
    excerpt: "Cabinet, wall, and reception transformations that skip the renovation. Here's how a 1,200 sq ft Mississauga office got a full visual refresh in under 48 hours.",
    date: "2026-05-10",
    author: "Toronto Custom Signs & Wraps",
    image: `${ASSET}/9h5z9lk7_20250829_193409.jpg`,
    tags: ["Architectural Wraps", "Mississauga", "Case Study"],
    readMinutes: 4,
    content: [
      { type: "p", text: "A Mississauga professional services office near Square One reached out wanting a brand-aligned refresh — but with one constraint: no closure, no contractors, no permits. Their landlord wouldn't allow renovation, and even if they had, the build-out would have run 4–6 weeks and well over $30,000." },
      { type: "p", text: "Architectural wraps were the obvious answer. Here's exactly how the project ran, what materials we used, and what the finished space looks like." },

      { type: "h2", text: "Day 1 — Site visit and design approval" },
      { type: "p", text: "We started with a 45-minute on-site assessment: measuring every surface they wanted wrapped, photographing existing finishes, and discussing brand colors. The client chose three premium 3M Di-Noc films — a warm walnut wood-grain for the reception desk, a soft matte stone for the kitchenette cabinets, and a brushed charcoal metal for accent walls." },
      { type: "p", text: "Within 48 hours we delivered a digital visual mockup so the client could approve the look before we cut a single piece of material." },

      { type: "h2", text: "Day 2 — Production and prep" },
      { type: "list", items: [
        "Material cut to exact size in-shop (eliminates field waste)",
        "Surfaces wiped down with isopropyl alcohol and inspected for grease or texture",
        "All cabinet hardware removed and bagged for clean reinstallation",
      ]},

      { type: "h2", text: "Day 3 — Install" },
      { type: "p", text: "Two installers, one site, no business interruption. We covered the reception desk and counter (4 hours), kitchenette cabinets (3 hours), and two accent walls (5 hours). All done by 6:30 pm. The office reopened the next morning looking like a completely different space." },

      { type: "h2", text: "What it cost — and what renovation would have cost" },
      { type: "callout", text: "Total project: $8,400 vs. estimated renovation $32,000+. Same visual impact, 1/4 the cost, 1/15 the downtime." },

      { type: "h2", text: "Why this works for Mississauga businesses" },
      { type: "p", text: "Most Mississauga office leases prohibit renovation without landlord approval — which often takes weeks and adds cost. Architectural wraps fall under \"reversible cosmetic improvements\" and almost always get approved without paperwork. When the lease ends or the tenant rebrands, the wrap removes cleanly without damaging the original surface." },

      { type: "h2", text: "Considering an office refresh in Mississauga?" },
      { type: "p", text: "We provide free on-site assessments and visual mockups for offices, retail, and commercial interiors across Mississauga, Erin Mills, Streetsville, and the Airport Corporate Centre. Most projects scoped, designed, and installed within 7–14 days." },
    ],
  },

  {
    slug: "channel-letter-signs-toronto-permits-costs",
    title: "Channel Letter Signs in Toronto: Permits, Timelines & Costs",
    excerpt: "Thinking about a channel letter sign for your Toronto storefront? Here's the realistic timeline, permit process, and what most businesses end up paying.",
    date: "2026-05-05",
    author: "Toronto Custom Signs & Wraps",
    image: `${ASSET}/smcw77sw_20260112_170103.jpg`,
    tags: ["Custom Signs", "Toronto", "Channel Letters"],
    readMinutes: 5,
    content: [
      { type: "p", text: "Channel letter signs — the dimensional, often-illuminated letters mounted to a building facade — are the gold standard for Toronto storefronts. They look premium, they read clearly from across the street, and they hold up to 10+ years of GTA weather. But they're not the cheapest signage option, and they involve permits. Here's exactly what you should expect." },

      { type: "h2", text: "How much do channel letter signs cost in Toronto?" },
      { type: "p", text: "Most Toronto storefronts spend $3,500 to $12,000 for a complete channel letter sign installed, depending on three factors:" },
      { type: "list", items: [
        "Letter height and width (each foot of letter height roughly doubles cost)",
        "Front-lit vs. halo-lit vs. double-lit (each illumination style adds material and electrical complexity)",
        "Mounting surface (brick, EIFS, and metal panel all require different mounting strategies)",
      ]},
      { type: "p", text: "For a 12-foot wide single-line sign with 18-inch letters and front-illumination, expect roughly $5,500–$7,000 turnkey. Larger signs or premium materials (polished metal returns, gold-leaf faces) push pricing higher." },

      { type: "h2", text: "Toronto sign permits: timeline" },
      { type: "p", text: "Every illuminated channel letter sign on a Toronto storefront requires a sign variance permit from the City of Toronto. Standard timelines:" },
      { type: "list", items: [
        "Application preparation and submission: 3–5 business days",
        "City review: 2–4 weeks",
        "Permit issued, sign approved for fabrication: same day",
      ]},
      { type: "p", text: "We handle the permit application end-to-end as part of every project — including the structural drawings, mounting details, and electrical specs required by the city. There's no separate fee for us to manage this; it's included." },

      { type: "h2", text: "Fabrication and install" },
      { type: "p", text: "Once the permit is issued, fabrication takes 10–14 business days. Installation is typically a one-day job: 4–6 hours on a lift for a standard storefront. We schedule installs early morning to avoid sidewalk and traffic disruption." },

      { type: "h2", text: "Total realistic timeline" },
      { type: "callout", text: "From first call to lit-up storefront: 4–6 weeks. Most of that is the city permit review — there's no shortcut, but we keep the rest of the process tight." },

      { type: "h2", text: "Common Toronto neighborhoods we serve" },
      { type: "p", text: "We've installed channel letter signs across King West, Queen West, Yonge-Dundas, Liberty Village, Yorkville, Bloor West Village, and Queen Street East. Each Business Improvement Area (BIA) has slightly different aesthetic standards we account for during design." },

      { type: "h2", text: "Ready to scope a sign for your storefront?" },
      { type: "p", text: "Free on-site measurement, free design mockup, transparent quote. We respond to GTA inquiries within 24 hours." },
    ],
  },

  {
    slug: "frosted-window-film-vs-blinds-office-privacy",
    title: "Frosted Window Film vs. Roller Blinds: Which Wins for Office Privacy?",
    excerpt: "Both block sightlines. Only one keeps your office bright, doubles as branding, and costs less in the long run. Here's a side-by-side breakdown.",
    date: "2026-04-28",
    author: "Toronto Custom Signs & Wraps",
    image: `${ASSET}/jl7hpxzp_20260129_102049.jpg`,
    tags: ["Window Film", "Office Design", "Comparison"],
    readMinutes: 3,
    content: [
      { type: "p", text: "If you've ever sat in a Toronto glass-walled boardroom feeling like everyone in the corridor could read your laptop, you've experienced the privacy problem of modern offices. There are two common solutions: roller blinds (or vertical shades) and frosted window film. Here's how they actually compare across the metrics that matter." },

      { type: "h2", text: "Privacy effectiveness" },
      { type: "p", text: "Both work. Roller blinds give full opacity when fully down. Frosted film gives permanent partial opacity — you can't see clear detail through it, but it diffuses light and silhouettes. For most office privacy needs (preventing screen-reading from outside, blocking direct sightlines into meeting rooms), frosted film is more than enough." },

      { type: "h2", text: "Natural light" },
      { type: "p", text: "Roller blinds: when they're down, your office goes dark. People raise them, and then privacy disappears. Frosted film: permanently lets daylight through while blocking visibility. Your office stays bright all day, every day. This single point is why most modern offices choose film." },

      { type: "h2", text: "Cost over 10 years" },
      { type: "list", items: [
        "Quality commercial roller blinds: $400–$800 per window installed. Replacement every 5–7 years (UV damage, mechanism wear).",
        "Frosted window film: $250–$500 per standard office window installed. Lifespan 10+ years with no moving parts to fail.",
      ]},
      { type: "callout", text: "On a typical 10-window office floor, frosted film saves $2,000–$4,000 over 10 years vs. blinds — and you get branding and design value on top." },

      { type: "h2", text: "Branding and design" },
      { type: "p", text: "Blinds are functional, not visual. Frosted film can be plain or transformed into a brand statement — gradient patterns, your logo subtly etched into the glass, decorative motifs that signal the company's aesthetic. We design custom patterns that look intentional, not stuck-on." },

      { type: "h2", text: "Maintenance" },
      { type: "p", text: "Blinds collect dust, snap mechanisms, fade. Frosted film is wipe-down-clean glass — no moving parts, no fabric, no maintenance beyond annual glass cleaning." },

      { type: "h2", text: "When blinds still win" },
      { type: "p", text: "If you need full blackout for a media or presentation room, you need blinds (or a blackout-rated film, which is more expensive than frosted). For 90% of office privacy needs, frosted film is the better choice." },

      { type: "h2", text: "Want a quote for your office?" },
      { type: "p", text: "We install frosted privacy film, branded etches, and gradient patterns across Toronto and the GTA. Free site visit, free mockup, no obligation." },
    ],
  },
];

export function getPost(slug) {
  return BLOG_POSTS.find(p => p.slug === slug);
}

export function getRelatedPosts(slug, limit = 2) {
  const current = getPost(slug);
  if (!current) return [];
  return BLOG_POSTS
    .filter(p => p.slug !== slug)
    .sort((a, b) => {
      const aShared = a.tags.filter(t => current.tags.includes(t)).length;
      const bShared = b.tags.filter(t => current.tags.includes(t)).length;
      return bShared - aShared;
    })
    .slice(0, limit);
}
